import { runtimeEnvironment } from '@/lib/env';
/**
 * Structured Logger for Eclipse Accounting
 *
 * Provides consistent, structured logging across the application
 * using pino for high-performance JSON logging.
 *
 * Features:
 * - JSON-formatted logs for production (easy to parse with log aggregators)
 * - Pretty-printed logs for development
 * - Contextual logging with child loggers
 * - Stripe-specific logging helpers
 * - Performance timing utilities
 */

import pino, {
  type Bindings,
  type ChildLoggerOptions,
  type Logger,
  type LoggerOptions,
} from 'pino';
import {
  sanitizeObservabilityData,
  sanitizeObservabilityString,
} from '@/lib/observability/privacy';

const isDev = runtimeEnvironment === 'development';

// Pino selects its browser entry when bundled for a client. Both entries use
// the same scrubber; the browser writes no debug/info output in production.
const baseConfig: LoggerOptions = {
  level: isDev ? 'debug' : 'info',
  base: { env: runtimeEnvironment, service: 'eclipse-accounting' },
  timestamp: pino.stdTimeFunctions.isoTime,
  formatters: {
    level: label => ({ level: label }),
    bindings: fields =>
      sanitizeObservabilityData(fields) as Record<string, unknown>,
    log: fields => sanitizeObservabilityData(fields) as Record<string, unknown>,
  },
  serializers: {
    err: sanitizeObservabilityData,
    error: sanitizeObservabilityData,
  },
  hooks: {
    logMethod(args, method) {
      return method.apply(
        this,
        args.map(value =>
          typeof value === 'string' ? sanitizeObservabilityString(value) : value
        ) as Parameters<typeof method>
      );
    },
  },
  browser: {
    asObject: true,
    write(entry) {
      const level =
        'level' in entry && typeof entry.level === 'number' ? entry.level : 30;
      if (runtimeEnvironment === 'production' && level < 40) return;
      const method =
        level >= 50
          ? 'error'
          : level >= 40
            ? 'warn'
            : level <= 20
              ? 'debug'
              : 'info';
      // eslint-disable-next-line no-console
      console[method](sanitizeObservabilityData(entry));
    },
  },
};

// Pino replaces the bindings formatter for children. Scrub at each child
// boundary so context stays safe on both server and browser, including nesting.
function withSafeChildren(
  base: Logger,
  nativeChild: (
    this: Logger,
    bindings: Bindings,
    options?: ChildLoggerOptions
  ) => Logger = base.child
): Logger {
  Object.defineProperty(base, 'child', {
    configurable: true,
    value(bindings: Bindings, options?: ChildLoggerOptions) {
      return withSafeChildren(
        nativeChild.call(
          base,
          sanitizeObservabilityData(bindings) as Bindings,
          options
        ),
        nativeChild
      );
    },
  });
  return base;
}

export const logger: Logger = withSafeChildren(pino(baseConfig));

/**
 * Create a child logger with additional context
 */
export function createChildLogger(context: Record<string, unknown>): Logger {
  return logger.child(context);
}

/**
 * Webhook-specific logger
 */
export const webhookLogger = logger.child({ module: 'webhook' });

/**
 * Invoice-specific logger
 */
export const invoiceLogger = logger.child({ module: 'invoice' });

/**
 * Subscription-specific logger
 */
export const subscriptionLogger = logger.child({ module: 'subscription' });

// ============================================================
// Performance Timing
// ============================================================

/**
 * Create a timer for measuring operation duration
 */
export function createTimer(operationName: string) {
  const startTime = Date.now();

  return {
    /**
     * End the timer and log the duration
     */
    end: (
      status: 'success' | 'error' = 'success',
      additionalData?: Record<string, unknown>
    ) => {
      const duration = Date.now() - startTime;
      logger.info(
        {
          event: 'timing',
          operation: operationName,
          durationMs: duration,
          status,
          ...additionalData,
        },
        `${operationName} completed in ${duration}ms`
      );
      return duration;
    },
  };
}

// ============================================================
// Console-Compatible Logger (drop-in replacement for console.*)
// ============================================================

/**
 * Formats variadic arguments into a single string message,
 * matching console.log behavior. Objects are JSON-stringified.
 */
function writeLog(level: 'info' | 'warn' | 'error' | 'debug', args: unknown[]) {
  const fields: Record<string, unknown> = {};
  const messages: string[] = [];
  for (const value of args) {
    if (value instanceof Error) fields.err = value;
    else if (value && typeof value === 'object' && !Array.isArray(value))
      Object.assign(fields, value);
    else if (value !== undefined) messages.push(String(value));
  }
  logger[level](fields, messages.join(' '));
}

/** Compatibility with console-style callers, without losing error/context fields. */
export const log = {
  info: (...args: unknown[]) => writeLog('info', args),
  warn: (...args: unknown[]) => writeLog('warn', args),
  error: (...args: unknown[]) => writeLog('error', args),
  debug: (...args: unknown[]) => writeLog('debug', args),
};
